import { CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Divider } from 'shared/components/ui/divider';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css } from 'styled-components';

export const Panel = styled.div`
  display: flex;

  width: 100%;
  height: 70vh;
  max-height: 100%;
  min-height: 0;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;
  min-width: 0;
`;

export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;

  padding: ${({ theme }) =>
    `${theme.spacing(3.5)} ${theme.spacing(4)}
    ${theme.spacing(3)} ${theme.spacing(6)}`};
`;

export const ContentDivider = styled(Divider)`
  margin: 0 ${({ theme }) => theme.spacing(6)};

  width: auto;
`;

export const ContentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};

  flex: 1;
  min-height: 0;
  overflow-y: auto;

  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(6)}`};
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

// Every settings panel shares one label column so its fields line up across sections.
const SETTING_LABEL_WIDTH = '25rem';

export const SettingItem = styled.div<{ $autoWidthField?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 3rem;

  & > :last-child {
    flex: ${({ $autoWidthField }) => ($autoWidthField ? '0 0 auto' : 1)};
    min-width: 0;
  }
`;

export const SettingLabel = styled.label`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  width: ${SETTING_LABEL_WIDTH};
  flex-shrink: 0;
`;

export const SubSection = styled.div``;

export const SplitSectionRow = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 30px;
`;

export const SecondaryTextTypography = styled(Typography)<{
  $canCopy?: boolean;
}>`
  color: ${({ theme }) => theme.palette.text.secondary};

  ${({ $canCopy }) =>
    $canCopy &&
    css`
      user-select: text;
      -ms-user-select: text;
      -moz-user-select: text;
      -webkit-user-select: text;
    `};
`;

export const ButtonLoader = styled(CircularProgress)``;

export const SubscriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};

  width: 100%;
`;

export const SubscriptionHeader = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: space-between;
  align-items: center;

  color: ${({ theme }) => theme.palette.text.primary};
`;

export const SubscriptionHeaderContent = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;

  min-width: 0;
`;

export const SubscriptionIcon = styled.span`
  font-size: 2.4rem;
  line-height: 62.5%;
`;

export const SubscriptionHeaderTitle = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.h6)};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};

  overflow: hidden;

  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const Pill = styled.span<{
  $warning: boolean;
  $error: boolean;
  $success: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  display: inline-block;
  padding: ${({ theme }) => `0 ${theme.spacing(2)}`};

  border-radius: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) =>
    theme.palette.primaryExtended.opacityBackgroundBackground};

  color: ${({ theme }) => theme.palette.primaryExtended.opacityBackgroundText};
  white-space: nowrap;

  ${({ $warning, $success, $error, theme }) =>
    ($success || $warning || $error) &&
    css`
      background: ${$error
        ? theme.palette.error.light
        : $warning
          ? theme.palette.warning.light
          : theme.palette.success.light};
      color: ${$error
        ? theme.palette.error.dark
        : $warning
          ? theme.palette.warning.dark
          : theme.palette.success.dark};
    `};
`;

export const SubscriptionDescription = styled.span`
  display: block;
  ${({ theme }) => typographyToCss(theme.typography.overline)};
  margin-left: ${({ theme }) => theme.spacing(8)};

  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: pre-wrap;
`;

// The label cell for rows whose label carries an info tooltip; same column as SettingLabel.
export const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};

  width: ${SETTING_LABEL_WIDTH};
  flex-shrink: 0;
`;

export const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  svg {
    width: 1.6rem;
    height: 1.6rem;
  }
`;
